import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './BrandVisualStyleSelection.module.scss'
import cx from 'classnames'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { useEffect, useState } from 'react'
import { VisualStyle } from 'types'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { GenerationLoading } from './GenerationLoading'

export interface StyleGenerated extends VisualStyle {
  styleUrl: string
}

export interface GenerationStageProps {
  onBack: () => void
  sourceImage: MediaFile
  visualStyles: VisualStyle[]
  brandKitId: number
  onGenerationComplete: (stylesGenerated: StyleGenerated[]) => void
}

export const GenerationStage = ({
  onBack,
  sourceImage,
  visualStyles,
  brandKitId,
  onGenerationComplete,
}: GenerationStageProps) => {
  const [generationDone, setGenerationDone] = useState<boolean>(false)
  const [transformedImageLoaded, setTransformedImageLoaded] = useState<boolean>(false)
  const styled1 = useStyledImage(sourceImage, visualStyles[0].id, brandKitId)
  const styled2 = useStyledImage(sourceImage, visualStyles[1]?.id, brandKitId)
  const styled3 = useStyledImage(sourceImage, visualStyles[2]?.id, brandKitId)

  const handleBack = () => {
    onBack()
  }

  const isLoading = styled1.isLoading || styled2.isLoading || styled3.isLoading || !transformedImageLoaded

  useEffect(() => {
    if (styled1.styledUrl && styled2.styledUrl && styled3.styledUrl && !isLoading && !generationDone) {
      setGenerationDone(true)
      onGenerationComplete([
        { styleUrl: styled1.styledUrl, ...visualStyles[0] },
        { styleUrl: styled2.styledUrl, ...visualStyles[1] },
        { styleUrl: styled3.styledUrl, ...visualStyles[2] },
      ])
    }
  }, [
    onGenerationComplete,
    styled1.styledUrl,
    styled2.styledUrl,
    styled3.styledUrl,
    visualStyles,
    isLoading,
    generationDone,
  ])

  return (
    <div className={Styles.selectImageStep}>
      <div className={Styles.leftSide}>
        <div className={Styles.title}>
          <Heading level={1} color="var(--dark-80)">
            Professional photography from any source
          </Heading>
          <div className={Styles.description}>
            <Text variant="primary" color="var(--dark-40)">
              Next, we'll create a professional visual style for all your content
            </Text>
          </div>
        </div>
        <div className={Styles.content}>
          <div className={Styles.originImageContainer}>
            <img src={sourceImage.url} alt="real origin" className={Styles.originImage} />
            <div className={cx(Styles.sourceTag, Styles.imageTag)}>Source Image</div>
          </div>
        </div>
        <div className={Styles.footer}>
          <Button variant="tertiary" size="lg" onPress={handleBack}>
            Back
          </Button>
          <Button size="lg" isDisabled={isLoading}>
            Continue
          </Button>
        </div>
      </div>
      <div className={Styles.transformedImageContainer}>
        <GenerationLoading
          sourceImage={sourceImage}
          stylesGeneration={styled1}
          onTransformedImageLoaded={setTransformedImageLoaded}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
